import { useEffect, useState } from "react";
import { toast } from "sonner";
import { enablePush, getChat, sessionHandoff } from "./live";

export const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

export const detectPlatform = () => {
  const ua = navigator.userAgent || "";
  const ios = /iPhone|iPad|iPod/i.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  if (/Instagram|FBAN|FBAV|FB_IAB|FBIOS|Line\/|TikTok|musical_ly|BytedanceWebview|Snapchat|Twitter|Kwai/i.test(ua)) return ios ? "inapp-ios" : "inapp-android";
  if (!ios && /Android/i.test(ua) && /; wv\)|Version\/[\d.]+ Chrome\//i.test(ua)) return "inapp-android";
  if (ios) {
    if (/CriOS/i.test(ua)) return "ios-chrome";
    if (/FxiOS|EdgiOS|OPiOS|YaBrowser|DuckDuckGo/i.test(ua)) return "ios-other";
    return "ios-safari";
  }
  if (/SamsungBrowser/i.test(ua)) return "android-samsung";
  if (/Firefox|OPR|EdgA|MiuiBrowser|UCBrowser/i.test(ua)) return "android-other";
  return "android-chrome";
};

export const chromeIntent = () => {
  const url = new URL(window.location.href);
  url.hash = "";
  const lc = sessionHandoff();
  if (lc) url.searchParams.set("lc", lc);
  return `intent://${url.host}${url.pathname}${url.search}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(url.href)};end`;
};

export const copyLink = async (browser) => {
  const url = window.location.href;
  try { await navigator.clipboard.writeText(url); } catch {
    const t = document.createElement("textarea");
    t.value = url; document.body.appendChild(t); t.select(); document.execCommand("copy"); t.remove();
  }
  toast.success(`Link copiado! Abra o ${browser} e cole na barra de endereço.`);
};

let deferred = null;
let installed = false;
const subs = new Set();
const notify = () => subs.forEach((f) => f());
const PENDING = "fm_installed_pending";
let reported = false;

export const reportInstalled = () => {
  if (reported) return;
  const chat = getChat();
  let pending = false;
  try { pending = !!localStorage.getItem(PENDING); } catch { /* ignore */ }
  if (!(isStandalone() || pending) || !chat?.hasSession() || typeof chat.markInstalled !== "function") return;
  reported = true;
  Promise.resolve(chat.markInstalled())
    .then(() => { try { localStorage.removeItem(PENDING); } catch { /* ignore */ } })
    .catch(() => { reported = false; });
};

const askPush = async () => {
  const chat = getChat();
  try {
    const cfg = await chat.config();
    if (cfg?.vapid_public && (await enablePush(chat, cfg))) toast.success("Avisos ativados!");
  } catch {
    toast.error("Não foi possível ativar os avisos.");
  }
};

export const initInstall = () => {
  installed = isStandalone();
  window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferred = e; notify(); });
  window.addEventListener("appinstalled", () => {
    installed = true;
    deferred = null;
    try { localStorage.setItem(PENDING, "1"); } catch { /* ignore */ }
    reportInstalled();
    notify();
    const canPush = getChat()?.hasSession() && "Notification" in window && Notification.permission === "default";
    toast.success("App instalado!", canPush
      ? { duration: 15000, description: "Quer receber avisos quando responderem?", action: { label: "Ativar avisos", onClick: askPush } }
      : undefined);
  });
};

export const useInstall = () => {
  const [, setTick] = useState(0);
  const [help, setHelp] = useState(false);

  useEffect(() => {
    const f = () => setTick((x) => x + 1);
    subs.add(f);
    return () => { subs.delete(f); };
  }, []);

  const install = async () => {
    if (!deferred) { setHelp(true); return; }
    const e = deferred;
    e.prompt();
    const { outcome } = await e.userChoice;
    deferred = null;
    if (outcome === "accepted") installed = true;
    notify();
  };

  return { installed, install, help, closeHelp: () => setHelp(false) };
};
